import {
    type Item,
    type MediaItem,
    type ProductData,
    type RelatedVariant
} from "@/lib/content-service/types/ContentServiceProduct"
import { type DisplayResponse } from "@/lib/depict-client/types/DisplayResponse"
import { type Variant } from "@/lib/depict-client/types/Variant"
import formatPrice from "./formatPrice"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CurrencyFormat {
    name: string
    prefix: string
    suffix: string
    decimalPoint: string
    thousandsSeparator: string
    decimalDigits: string
    uri: string
}

// ---------------------------------------------------------------------------
// Media utilities
// ---------------------------------------------------------------------------

export function transformDepictMediaStructure(
    imageUrls: string[],
    media?: {
        standard?: Array<{ url: string }>
        full?: Array<{ url: string }>
        big?: Array<{ url: string }>
        small?: Array<{ url: string }>
        mini?: Array<{ url: string }>
    }
): MediaItem[] {
    if (media?.standard && media.standard.length > 0) {
        return media.standard.map(item => ({
            url: item.url,
            attributes: undefined
        }))
    }

    if (imageUrls && imageUrls.length > 0) {
        return imageUrls.map(url => ({
            url,
            attributes: undefined
        }))
    }

    return []
}

// ---------------------------------------------------------------------------
// Price formatting + preprocessing (combined single pass)
// Was: preprocessDepictDisplays + formatPricesInTransformedData
// ---------------------------------------------------------------------------

interface DisplaysContainer {
    displays: any[]
}

/**
 * Preprocesses and formats prices on each variant_display in a single pass:
 * - Saves price_before_discount_as_number from the numeric original_price before formatting
 * - Sets price_as_number from the numeric sale_price
 * - Formats sale_price and original_price to currency strings
 * - Trims image_urls to 2 when isPlp is false (recommendations/search contexts)
 */
export const formatDepictPrices = (
    data: DisplaysContainer,
    currentPricelist: CurrencyFormat | undefined,
    isPlp = false
) => {
    const formattedDisplays = data.displays.map((display: any) => {
        const formattedVariantDisplays = display.variant_displays.map((variant: any) => {
            const fallbackCurrencySuffix = variant?.currency
            const imageUrls =
                !isPlp &&
                Array.isArray(variant.image_urls) &&
                variant.image_urls.length > 2
                    ? variant.image_urls.slice(0, 2)
                    : variant.image_urls
            return {
                ...variant,
                image_urls: imageUrls,
                price_as_number: parseFloat(variant.sale_price),
                price_before_discount_as_number: parseFloat(variant.original_price),
                sale_price: formatPrice(
                    variant.sale_price.toString(),
                    currentPricelist,
                    fallbackCurrencySuffix
                ),
                original_price: formatPrice(
                    variant.original_price.toString(),
                    currentPricelist,
                    fallbackCurrencySuffix
                )
            }
        })
        return { ...display, variant_displays: formattedVariantDisplays }
    })

    return { ...data, displays: formattedDisplays }
}

// ---------------------------------------------------------------------------
// ProductData output
// Was: buildProductDataGroups (private) + transformDepictDisplayResponseToProductData
// ---------------------------------------------------------------------------

function buildProductDataGroups<T extends Variant>(
    items: T[],
    key: keyof T
): Array<{ key: string; items: ProductData[] }> {
    const colors: Record<string, number> = {}
    const productGroups = items.reduce((acc, item) => {
        const groupKey = String(item[key])
        const colorKey = String(item[key] + "_" + item.display_item_id)
        if (!acc.has(groupKey)) {
            acc.set(groupKey, [])
        }
        if (!colors[colorKey]) {
            acc.get(groupKey)!.push(item)
            colors[colorKey] = 1
        }
        return acc
    }, new Map<string, T[]>())

    return Array.from(productGroups.entries()).map(([groupKey, variants]) => {
        const primary = variants[0]

        const relatedVariants: RelatedVariant[] = variants
            .filter(v => v.product_id !== primary.product_id)
            .map(v => {
                const variantItem: Item = {
                    ean: "",
                    sku: v.transaction_product_id,
                    upc: "",
                    item: v.display_item_id ?? "",
                    name: v.size || "",
                    stock: v.in_stock ? "yes" : "no",
                    sizeId: "",
                    itemTableX: 0,
                    itemTableY: 0
                }

                return {
                    media: transformDepictMediaStructure(v.image_urls ?? []),
                    product: v.main_product_id,
                    name: v.title,
                    relation: "variant",
                    available: v.in_stock,
                    price: v.sale_price,
                    uri: v.uri,
                    color_swatch: {
                        hex: v.color_hex,
                        color_name: v.color_name
                    },
                    sku: v.transaction_product_id,
                    items: [variantItem],
                    lens: {
                        hex: v.lens_hex
                    },
                    variant_sort_order_sort_order: v.variant_sorder_order,
                    priceBeforeDiscount: v.original_price,
                    showAsOnSale: v.show_as_sale,
                    showAsNew: v.show_as_new,
                    benefitList: v.product_benefits_list ? [v.product_benefits_list] : [],
                    combinedBadges: v.badge,
                    sanity_inject_badge_json: v.sanity_inject_badge ?? undefined,
                    priceAsNumber: v.price_as_number,
                    priceBeforeDiscountAsNumber: v.price_before_discount_as_number || 0,
                    seasonal_merchandising: v.seasonal_merchandising ?? [],
                    englishName: v.english_title || ""
                }
            })

        const originalPrice = parseFloat(primary.original_price) || 0
        const salePrice = parseFloat(primary.sale_price) || 0
        const discountPercent =
            originalPrice > 0
                ? Math.round(((originalPrice - salePrice) / originalPrice) * 100)
                : 0

        const primaryItem: Item = {
            ean: "",
            sku: primary.transaction_product_id,
            upc: "",
            item: primary.display_item_id ?? "",
            name: primary.size || "",
            stock: primary.in_stock ? "yes" : "no",
            sizeId: "",
            itemTableX: 0,
            itemTableY: 0
        }

        const primaryProductData: ProductData = {
            sku: primary.transaction_product_id,
            uri: primary.uri,
            name: primary.title,
            price: primary.sale_price,
            product: primary.main_product_id,
            relation: "product",
            available: primary.in_stock,
            englishName: primary.english_title || "",
            media: transformDepictMediaStructure(primary.image_urls ?? []),
            showAsNew: primary.show_as_new,
            items: [primaryItem],
            brand: "",
            productSku: primary.transaction_product_id,
            showAsOnSale: primary.show_as_sale,
            centraProduct: primary.main_product_id,
            centraVariant: primary.variant_id,
            priceAsNumber: primary.price_as_number,
            discountPercent,
            relatedVariants,
            priceBeforeDiscount: primary.original_price,
            priceBeforeDiscountAsNumber: primary.price_before_discount_as_number || 0,
            lens: {
                hex: primary.lens_hex,
                lens_name: primary.lens_name
            },
            color_swatch: {
                hex: primary.color_hex,
                color_name: primary.color_name,
                filter_color: primary.filter_color
            },
            benefitList: primary.product_benefits_list
                ? [primary.product_benefits_list]
                : [],
            combinedBadges: primary.badge,
            sanity_inject_badge_json: primary.sanity_inject_badge ?? undefined,
            sanity_inject_general_short_text_json:
                primary.sanity_inject_general_short_text ?? undefined,
            fit_type_fit_type: primary.fit,
            productId: primary.main_product_id,
            product_listing_result_id: primary.product_listing_result_id,
            search_result_id: primary.search_result_id,
            recommendation_id: primary.recommendation_id,
            seasonal_merchandising: primary.seasonal_merchandising ?? []
        } as ProductData

        return {
            key: groupKey,
            items: [primaryProductData]
        }
    })
}

/**
 * Transforms a Depict display response into grouped ProductData arrays.
 * Used everywhere: PLP, search, recommendations, and carousel.
 *
 * Call formatDepictPrices on the data before passing it here.
 */
export function transformDepictToProductData<T extends DisplayResponse>(
    res: T | undefined | null,
    key: keyof Variant
): Array<{ key: string; items: ProductData[] }> {
    const items =
        res?.displays?.flatMap((display: any) => {
            const primaryVariant = display.variant_displays?.splice(
                display.variant_index,
                1
            )
            display.variant_displays.unshift(primaryVariant[0])
            return (
                display.variant_displays?.map((variant: Variant) => ({
                    ...variant,
                    search_result_id: display.search_result_id,
                    recommendation_id: display.recommendation_id,
                    product_listing_result_id: display.product_listing_result_id
                })) || []
            )
        }) || []

    return buildProductDataGroups(items, key)
}
